import React from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, CircleDashedIcon, SnowflakeIcon, ThermometerIcon } from 'lucide-react';
export type TelemetryStatus = 'verified' | 'pending' | 'unavailable' | 'exception';
interface ColdChainStatusProps {
  status: TelemetryStatus;
  temperature?: number;
  temperatureRange?: string;
  lastUpdated?: string;
  compact?: boolean;
}
const statusContent = {
  verified: {
    label: 'Temperature verified',
    detail: 'The delivery box is within the safe range.',
    icon: CheckCircle2Icon,
    className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    iconClassName: 'bg-emerald-600 text-white'
  },
  pending: {
    label: 'Temperature check in progress',
    detail: 'We will show a verified reading once the box is sealed.',
    icon: CircleDashedIcon,
    className: 'border-amber-200 bg-amber-50 text-amber-900',
    iconClassName: 'bg-amber-500 text-white'
  },
  unavailable: {
    label: 'Temperature reading unavailable',
    detail: 'Live sensor data is not connected for this delivery.',
    icon: ThermometerIcon,
    className: 'border-slate-200 bg-slate-50 text-slate-800',
    iconClassName: 'bg-slate-600 text-white'
  },
  exception: {
    label: 'Temperature check needs attention',
    detail: 'We paused the handoff while our pharmacy team reviews a reading outside the safe range.',
    icon: AlertTriangleIcon,
    className: 'border-rose-200 bg-rose-50 text-rose-900',
    iconClassName: 'bg-rose-600 text-white'
  }
} as const;
export function ColdChainStatus({
  status,
  temperature,
  temperatureRange = '2–8°C',
  lastUpdated,
  compact = false
}: ColdChainStatusProps) {
  const content = statusContent[status];
  const StatusIcon = content.icon;
  if (compact) {
    return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-bold ${content.className}`} data-id="element-1862" data-mp="144ury6jb4mn2">
        <SnowflakeIcon className="h-3 w-3" aria-hidden="true" data-id="element-1863" data-mp="bm7dh9ytg5uy" />
        {(status === 'verified' || status === 'exception') && temperature !== undefined ? `${temperature}°C · ${status === 'exception' ? 'review needed' : 'protected'}` : content.label}
      </span>;
  }
  return <section className={`rounded-xl border p-3.5 ${content.className}`} aria-label="Cold-chain delivery status" data-id="element-1864" data-mp="3uq82jeokv9w8">
      <div className="flex items-start gap-3" data-id="element-1865" data-mp="8crhrxob8b9d">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${content.iconClassName}`} data-id="element-1866" data-mp="8czv1qochil3">
          <StatusIcon className="h-5 w-5" aria-hidden="true" data-id="element-1867" data-mp="3gsjgw4moryq4" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-1868" data-mp="3buhzdo4ws7gr">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-1869" data-mp="3bvknxperdxti">
            <p className="text-sm font-bold" data-id="element-1870" data-mp="3qrsa2v9q99bf">{content.label}</p>
            <span className="text-xs font-semibold opacity-75" data-id="element-1871" data-mp="3lr4ia7docog5">Safe range: {temperatureRange}</span>
          </div>
          <p className="mt-1 text-xs leading-5 opacity-85" data-id="element-1872" data-mp="15yx5d0skmhly">{content.detail}</p>
          {(status === 'verified' || status === 'exception') && temperature !== undefined && <p className="mt-2 text-xs font-bold" data-id="element-1873" data-mp="18kepk7rth8ai">Latest box reading: {temperature}°C{lastUpdated ? ` · updated ${lastUpdated}` : ''}</p>}
          {status !== 'verified' && status !== 'exception' && lastUpdated && <p className="mt-2 text-xs font-semibold" data-id="element-1874" data-mp="zl1e59k4u9n5">Last update: {lastUpdated}</p>}
        </div>
      </div>
    </section>;
}